import { useState, useEffect } from 'react';
import type { ImgHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/utils';

interface EAdaptImageProps extends ImgHTMLAttributes<HTMLImageElement> {
    src: string;
}

const getBaseUrl = (u: string) => {
    if (!u) return '';
    try {
        const urlObj = new URL(u);
        return urlObj.origin + urlObj.pathname;
    } catch (e) {
        return u.split('?')[0];
    }
};

/**
 * 
 * 这个组件的作用是优化 img 标签在 oss 缩略图档位切换的时候，把一闪一闪的体验问题，优化位从模糊到清晰的效果
 * 
 */
export function EAdaptImage({ src, className, style, ...props }: EAdaptImageProps) {
    const [currentSrc, setCurrentSrc] = useState(src);
    const [prevSrc, setPrevSrc] = useState<string | null>(null);

    // 独立监控定时器，解决加载中断造成的死锁问题
    useEffect(() => {
        if (prevSrc) {
            const timer = setTimeout(() => {
                setPrevSrc(null);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [prevSrc, currentSrc]);

    useEffect(() => {
        if (!src) {
            setCurrentSrc(src);
            setPrevSrc(null);
            return;
        }

        if (src === currentSrc) {
            return;
        }

        if (!currentSrc) {
            setCurrentSrc(src);
            setPrevSrc(null);
            return;
        }

        // 如果不是同一张图片（比如用户完全替换了图片），直接切换，不使用过渡效果
        if (getBaseUrl(src) !== getBaseUrl(currentSrc)) {
            setCurrentSrc(src);
            setPrevSrc(null);
            return;
        }

        let isActive = true;
        const img = new window.Image();

        img.onload = () => {
            if (isActive) {
                // 保存旧图作为底层占位
                setPrevSrc(currentSrc);
                // 切换到新图
                setCurrentSrc(src);
            }
        };

        img.src = src;

        return () => {
            isActive = false;
        };
    }, [src, currentSrc]);

    return (
        <div className={cn("relative overflow-hidden", className)} style={style}>
            {/* 底层旧图，始终保持完全不透明 */}
            {prevSrc && (
                <img
                    src={prevSrc}
                    className="absolute inset-0 w-full h-full"
                    style={{ objectFit: 'inherit', objectPosition: 'inherit' }}
                    alt={props.alt}
                />
            )}

            {/* 上层新图，如果是从旧图切换过来的，就触发淡入动画 */}
            <img
                key={getBaseUrl(currentSrc)}
                src={currentSrc}
                className={cn(
                    "absolute inset-0 w-full h-full",
                    prevSrc ? "animate-fade-in-image" : ""
                )}
                style={{ objectFit: 'inherit', objectPosition: 'inherit' }}
                {...props}
            />
        </div>
    );
}
